<template>
  <div class="relative w-full" ref="containerRef">
    <!--
      Trigger. Lleva tabindex y rol de combobox porque antes era un <div> con
      @click y nada más: no se podía enfocar con Tab ni abrir sin mouse, así que
      cualquier formulario con un AppSelect cortaba el recorrido por teclado.
    -->
    <div
      ref="triggerRef"
      tabindex="0"
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-activedescendant="isOpen && resaltado >= 0 ? `${idListbox}-opt-${resaltado}` : undefined"
      @click="alternar"
      @keydown="onKeydownTrigger"
      @blur="isOpen = false"
      :class="['w-full px-3 py-2.5 rounded-lg bg-background-secondary text-text border border-border focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all cursor-pointer flex justify-between items-center', containerClass]"
      :style="customStyle"
    >
      <span class="truncate">{{ selectedLabel || 'Seleccionar...' }}</span>
      <i class="pi pi-chevron-down text-text-secondary text-sm ml-2 shrink-0 transition-transform" :class="{ 'rotate-180': isOpen }"></i>
    </div>

    <!-- Custom Dropdown Menu -->
    <div
      v-if="isOpen"
      :id="idListbox"
      role="listbox"
      ref="listaRef"
      class="absolute z-50 w-full mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto"
    >
      <div
        v-for="(opt, idx) in parsedOptions"
        :key="idx"
        :id="`${idListbox}-opt-${idx}`"
        role="option"
        :aria-selected="isSelected(opt.value)"
        @mousedown.prevent="selectOption(opt)"
        @mousemove="resaltado = idx"
        class="px-4 py-2 cursor-pointer text-text truncate transition-colors"
        :class="{
          'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-medium': isSelected(opt.value),
          'text-text-secondary cursor-not-allowed opacity-50': opt.disabled,
          'bg-hover-overlay': idx === resaltado && !isSelected(opt.value),
        }"
      >
        {{ opt.label }}
      </div>
    </div>

    <!-- Hidden Native Select to capture slots and bindings -->
    <select class="hidden" ref="nativeSelectRef" v-model="internalValue" @change="onNativeChange">
      <slot></slot>
    </select>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
  modelValue: { type: [String, Number, Boolean, Object], default: null },
  class: { type: String, default: '' },
  style: { type: [String, Object], default: '' }
});

const emit = defineEmits(['update:modelValue', 'change']);

const internalValue = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const containerRef = ref(null);
const nativeSelectRef = ref(null);
const triggerRef = ref(null);
const listaRef = ref(null);
const isOpen = ref(false);
const parsedOptions = ref([]);
/** Opción bajo el cursor de teclado. -1 = ninguna. */
const resaltado = ref(-1);
const idListbox = `appselect-${Math.random().toString(36).slice(2, 9)}`;

// We pass the class attribute to the trigger div
const containerClass = computed(() => {
  return props.class;
});
const customStyle = computed(() => props.style);

const updateOptions = () => {
  if (!nativeSelectRef.value) return;
  const optionEls = nativeSelectRef.value.querySelectorAll('option');
  const newOptions = [];
  optionEls.forEach(el => {
    // Vue 3 stores bound object values in _value
    let val = '_value' in el ? el._value : el.value;
    newOptions.push({
      value: val,
      label: el.textContent.trim(),
      disabled: el.disabled
    });
  });

  // Compare to prevent infinite update loops when re-rendering
  let hasChanged = newOptions.length !== parsedOptions.value.length;
  if (!hasChanged) {
    for (let i = 0; i < newOptions.length; i++) {
      if (
        newOptions[i].value !== parsedOptions.value[i].value ||
        newOptions[i].label !== parsedOptions.value[i].label ||
        newOptions[i].disabled !== parsedOptions.value[i].disabled
      ) {
        hasChanged = true;
        break;
      }
    }
  }

  if (hasChanged) {
    parsedOptions.value = newOptions;
  }
};

let observer = null;

onMounted(() => {
  updateOptions();
  
  // Observe changes in the options (e.g. v-for updates)
  observer = new MutationObserver(() => {
    updateOptions();
  });
  
  if (nativeSelectRef.value) {
    observer.observe(nativeSelectRef.value, { childList: true, subtree: true, characterData: true, attributes: true });
  }

  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  if (observer) observer.disconnect();
  document.removeEventListener('click', handleClickOutside);
});

const handleClickOutside = (event) => {
  if (containerRef.value && !containerRef.value.contains(event.target)) {
    isOpen.value = false;
  }
};

const isSelected = (optValue) => {
  if (optValue === props.modelValue) return true;
  if (typeof optValue === 'object' && optValue !== null && typeof props.modelValue === 'object' && props.modelValue !== null) {
    try { return JSON.stringify(optValue) === JSON.stringify(props.modelValue); } catch (e) { return false; }
  }
  if (String(optValue) === String(props.modelValue)) return true;
  return false;
};

const selectedLabel = computed(() => {
  const opt = parsedOptions.value.find(o => isSelected(o.value));
  return opt ? opt.label : '';
});

const selectOption = (opt) => {
  if (opt.disabled) return;
  emit('update:modelValue', opt.value);
  emit('change', { target: { value: opt.value } });
  isOpen.value = false;
  resaltado.value = -1;
  // Al elegir con el mouse el foco queda en el trigger, así el recorrido por
  // teclado puede seguir desde acá sin volver a agarrar el mouse.
  triggerRef.value?.focus();
};

const onNativeChange = (e) => {
  // Just in case
  emit('update:modelValue', e.target.value);
  emit('change', e);
};

// ═══════════════════════════════════════════════════════════════════════════
// TECLADO
//
// El objetivo es que un operario pueda cargar sin soltar el teclado, así que
// esto imita a un <select> nativo: Enter/Espacio/flechas abren, las flechas
// mueven, Enter confirma, Escape cancela y Tab sigue de largo.
// ═══════════════════════════════════════════════════════════════════════════

const indiceSeleccionado = () => parsedOptions.value.findIndex(o => isSelected(o.value));

const abrir = () => {
  isOpen.value = true;
  // Arranca sobre lo ya elegido: así repetir la misma carga es Enter + Enter.
  resaltado.value = Math.max(indiceSeleccionado(), 0);
  verResaltado();
};

const cerrar = () => { isOpen.value = false; resaltado.value = -1; };
const alternar = () => (isOpen.value ? cerrar() : abrir());

/** Mantiene visible la opción resaltada dentro del panel scrolleable. */
const verResaltado = () => {
  nextTick(() => {
    listaRef.value?.querySelector(`#${idListbox}-opt-${resaltado.value}`)
      ?.scrollIntoView({ block: 'nearest' });
  });
};

/** Se saltea las deshabilitadas para que las flechas nunca queden trabadas. */
const mover = (paso) => {
  const n = parsedOptions.value.length;
  if (!n) return;
  let i = resaltado.value;
  for (let intentos = 0; intentos < n; intentos++) {
    i = (i + paso + n) % n;
    if (!parsedOptions.value[i].disabled) { resaltado.value = i; verResaltado(); return; }
  }
};

// Typeahead: escribir "ef" salta a "Efectivo", como en un select nativo.
let buffer = '', bufferTimer = null;
const buscarPorTexto = (letra) => {
  clearTimeout(bufferTimer);
  buffer += letra.toLowerCase();
  bufferTimer = setTimeout(() => { buffer = ''; }, 700);
  const desde = resaltado.value >= 0 ? resaltado.value : 0;
  const n = parsedOptions.value.length;
  for (let k = 0; k < n; k++) {
    const i = (desde + k) % n;
    const o = parsedOptions.value[i];
    if (!o.disabled && o.label.toLowerCase().startsWith(buffer)) {
      resaltado.value = i;
      verResaltado();
      return;
    }
  }
};

const onKeydownTrigger = (e) => {
  if (e.key === 'Tab') { cerrar(); return; } // no se bloquea: Tab tiene que seguir
  // Ctrl+Enter es "confirmar el formulario": no lo consume el select.
  if (e.ctrlKey || e.metaKey || e.altKey) return;

  // Toda tecla que consume el select frena la propagación. Si no, el mismo
  // Enter que abre el desplegable lo tomaría además el "Enter avanza" del
  // formulario y saltaría al campo siguiente sin dejarte elegir.
  if (!isOpen.value) {
    // Enter cerrado NO abre: deja burbujear para que el formulario avance al
    // campo siguiente. Si abriera, el operario quedaría atrapado en el select
    // reabriéndolo con cada Enter y nunca podría pasar de largo.
    // Para abrir están el espacio, las flechas y escribir la primera letra.
    if ([' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault(); e.stopPropagation(); abrir();
    } else if (e.key.length === 1) {
      e.preventDefault(); e.stopPropagation(); abrir(); buscarPorTexto(e.key);
    }
    return;
  }
  e.stopPropagation();

  switch (e.key) {
    case 'ArrowDown': e.preventDefault(); mover(1); break;
    case 'ArrowUp': e.preventDefault(); mover(-1); break;
    case 'Home': e.preventDefault(); resaltado.value = -1; mover(1); break;
    case 'End': e.preventDefault(); resaltado.value = 0; mover(-1); break;
    case 'Enter':
      e.preventDefault();
      // Se frena la propagación para que el Enter que confirma la opción no
      // lo tome además el formulario y salte dos campos de una.
      e.stopPropagation();
      if (parsedOptions.value[resaltado.value]) selectOption(parsedOptions.value[resaltado.value]);
      break;
    case 'Escape': e.preventDefault(); e.stopPropagation(); cerrar(); break;
    default:
      if (e.key.length === 1) { e.preventDefault(); buscarPorTexto(e.key); }
  }
};

// Permite que un contenedor externo lo enfoque como a cualquier input.
defineExpose({ focus: () => triggerRef.value?.focus() });
</script>
